import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { hasMany, options } from '@apexdesigner/schema-tools';

export const npmPackage = z
  .object({
    id: z.number().hidden().describe('Unique identifier'),

    name: z
      .string()
      .nullable()
      .optional()
      .describe('Name - Full npm name including scope, e.g.'),

    publishRepository: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Publish Repository - `owner/repo` of the GitHub repository whose workflow may publish this package\n\nThe whole of trusted publishing's authorization, and the reason it is\nsafe: only an administrator can set it, so it is never established\nfrom anything a publisher sends. Unset means no workflow can publish\nthis package, whatever id token it presents.",
      ),

    publishWorkflow: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Publish Workflow - Workflow filename that may publish, e.g. ci-cd.yml, matched against the workflow_ref claim',
      ),

    publishEnvironment: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Publish Environment - GitHub deployment environment the publishing job must declare\n\nOptional, matching npm's own trusted publisher configuration. Setting\nit is what makes a branch restriction stick: GitHub blocks a run from\na branch outside the environment's list, and a workflow that deletes\nthe `environment:` line to dodge that arrives with no claim and is\nrefused here.",
      ),

    description: z
      .string()
      .nullable()
      .optional()
      .describe('Description - From the most recent publish'),

    access: options([
      {
        value: 'Standard',
        name: 'Standard',
        description: 'Readable by any user holding an active standard grant',
      },
      {
        value: 'Restricted',
        name: 'Restricted',
        description: 'Readable only via an entitlement naming the package',
      },
      {
        value: 'Development',
        name: 'Development',
        description:
          'Not yet released; team roles only, hidden from everyone else',
      },
    ])
      .nullable()
      .optional()
      .describe('Access - Access tier controlling who may read this package'),

    deprecatedMessage: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Deprecated Message - Set when the whole package is deprecated',
      ),

    upstreamSyncedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe(
        'Upstream Synced At - When the upstream sync last copied this package, for display',
      ),

    distTagsJson: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe('Dist Tags Json - JSON map of dist-tag name to version'),

    category: options([
      {
        value: 'Design',
        name: 'Design',
        description: 'Design libraries added to a project with ad3 deps add',
      },
      {
        value: 'Template',
        name: 'Template',
        description:
          'Project and library templates that new repositories are created from',
      },
      {
        value: 'Supporting',
        name: 'Supporting',
        description:
          'Packages applications depend on but nobody installs by hand',
      },
      {
        value: 'Tool',
        name: 'Tool',
        description: 'Command line tools, generators, and editor integrations',
      },
      {
        value: 'Hidden',
        name: 'Hidden',
        description:
          'Left off the packages page and out of search; still installable',
      },
      {
        value: 'Uncategorized',
        name: 'Uncategorized',
        description: 'No recognised keyword and no administrator override',
      },
    ])
      .nullable()
      .optional()
      .describe(
        "Category - Derived from the latest version's keywords, or the override when one is set",
      ),

    categoryOverride: options([
      {
        value: 'Design',
        name: 'Design',
        description: 'Design libraries added to a project with ad3 deps add',
      },
      {
        value: 'Template',
        name: 'Template',
        description:
          'Project and library templates that new repositories are created from',
      },
      {
        value: 'Supporting',
        name: 'Supporting',
        description:
          'Packages applications depend on but nobody installs by hand',
      },
      {
        value: 'Tool',
        name: 'Tool',
        description: 'Command line tools, generators, and editor integrations',
      },
      {
        value: 'Hidden',
        name: 'Hidden',
        description:
          'Left off the packages page and out of search; still installable',
      },
      {
        value: 'Uncategorized',
        name: 'Uncategorized',
        description: 'No recognised keyword and no administrator override',
      },
    ])
      .nullable()
      .optional()
      .describe(
        'Category Override - Pins the category regardless of keywords; set by an administrator',
      ),

    latestVersion: z
      .string()
      .nullable()
      .optional()
      .describe('Latest Version - The version the latest dist-tag points at'),

    latestPublishedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('Latest Published At - When the latest version was published'),

    latestNewsSummary: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Latest News Summary - The summary of the latest version's release note, for the catalog row",
      ),

    hasDocs: z
      .boolean()
      .nullable()
      .optional()
      .describe('Has Docs - Whether the latest version carries a docs folder'),

    hasNews: z
      .boolean()
      .nullable()
      .optional()
      .describe('Has News - Whether the latest version carries a release note'),

    registryTokens: hasMany('RegistryToken'),

    versions: hasMany('PackageVersion'),

    packageDocs: hasMany('PackageDoc'),
  })
  .describe('A named npm package hosted by the registry.')
  .as('NpmPackage');

export { npmPackage as npmPackageSchema };
